import React, { useState, useRef, useEffect } from "react";
import { useDrag, useDrop } from "react-dnd";
import {
  IconGripVertical,
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconArrowsExchange,
  IconChevronDown,
} from "@tabler/icons-react";
import MoonLoader from "react-spinners/MoonLoader";
import { useCRM } from "../CRMContext";
import ContactCard from "./ContactCard";

const ItemTypes = { COLUMN: "COLUMN", CONTACT: "CONTACT" };

const formatCurrency = (value) => {
  if (!value && value !== 0) return "";
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }).format(value);
};

const Column = ({ column, index, onAddContact, onEditColumn, onDeleteColumn, onMoveAll }) => {
  const { state, loadMoreContacts, moveContactLocal, moveContactAPI, reorderColumnsLocal, reorderColumnsAPICall } = useCRM();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const ref = useRef(null);

  const contacts = state.contactsByColumn[column.id] || [];
  const isLoadingMore = state.loadingMoreByColumn[column.id] || false;
  const hasMore = state.hasMoreByColumn[column.id] || false;

  // Close menu on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const color = column.color || "#3b82f6";

  // Drop zone for CONTACT and COLUMN
  const [{ isOver }, drop] = useDrop({
    accept: [ItemTypes.CONTACT, ItemTypes.COLUMN],
    drop: (item, monitor) => {
      if (item.type === ItemTypes.CONTACT && !monitor.didDrop()) {
        // Card dropped on column body (not on a specific card)
        const destContacts = state.contactsByColumn[column.id] || [];
        moveContactLocal(item.contactId, item.sourceColumnId, column.id, destContacts.length);
        moveContactAPI(item.contactId, column.id, destContacts.length);
        return { dropped: true };
      }
      if (item.type === ItemTypes.COLUMN) {
        // Column dropped — persist to API
        reorderColumnsAPICall(state.columns);
        return { dropped: true };
      }
    },
    hover: (item, monitor) => {
      if (item.type === ItemTypes.COLUMN) {
        if (item.index === index) return;
        const newColumns = Array.from(state.columns);
        const [moved] = newColumns.splice(item.index, 1);
        newColumns.splice(index, 0, moved);
        reorderColumnsLocal(newColumns);
        item.index = index;
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  // Drag handle for COLUMN
  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ItemTypes.COLUMN,
    item: { type: ItemTypes.COLUMN, columnId: column.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // dragPreview on container, drag on handle
  dragPreview(drop(ref));

  return (
    <div ref={ref} className={`crm-column ${isDragging ? "crm-column--dragging" : ""}`}>
      <div className="crm-column__color-bar" style={{ background: color }} />

      <div className="crm-column__header">
        <div className="crm-column__drag-handle" ref={drag}>
          <IconGripVertical size={16} />
        </div>

        <h4 className="crm-column__title">{column.name}</h4>

        <div className="crm-column__meta">
          <span className="crm-column__count">{column.contacts_count ?? contacts.length}</span>
          {column.total_value > 0 && (
            <span className="crm-column__value">{formatCurrency(column.total_value)}</span>
          )}
        </div>

        <div className="crm-column__actions" ref={menuRef}>
          <button className="crm-column__menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
            <IconDotsVertical size={16} />
          </button>

          {menuOpen && (
            <div className="crm-column__menu">
              <button className="crm-column__menu-item" onClick={() => { setMenuOpen(false); onEditColumn(column); }}>
                <IconEdit size={16} /> Editar
              </button>
              <button className="crm-column__menu-item" onClick={() => { setMenuOpen(false); onMoveAll?.(column); }}>
                <IconArrowsExchange size={16} /> Mover todos
              </button>
              <button className="crm-column__menu-item crm-column__menu-item--danger" onClick={() => { setMenuOpen(false); onDeleteColumn(column); }}>
                <IconTrash size={16} /> Excluir
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={`crm-column__body ${isOver ? "crm-column__body--drag-over" : ""}`}>
        {contacts.map((contact, contactIndex) => (
          <ContactCard key={contact.id} contact={contact} index={contactIndex} columnId={column.id} columnColor={color} />
        ))}
      </div>

      {/* Load more button */}
      {hasMore && (
        <button
          className="crm-column__load-more"
          onClick={() => loadMoreContacts(column.id)}
          disabled={isLoadingMore}
        >
          {isLoadingMore ? (
            <MoonLoader color="#3b82f6" size={14} />
          ) : (
            <>
              <IconChevronDown size={14} />
              Carregar mais
            </>
          )}
        </button>
      )}

      <button className="crm-column__add-btn" onClick={() => onAddContact(column.id)}>
        <IconPlus size={14} /> Adicionar
      </button>
    </div>
  );
};

export default Column;
